/**
 * §18.7 — the Form 941 worksheet: the numbers to copy onto the return for one
 * quarter (payroll stage 4).
 *
 * A WORKSHEET, AND IT SAYS SO FIRST. The figures are GET /payroll/forms/941's
 * (apps/api/src/payroll/form-941.ts, from the Instructions for Form 941
 * (03/2026)); this screen lays them out in the form's own order and words. It
 * has no control that files, submits or marks anything filed, and its opening
 * sentence says the return is the business's to file.
 *
 * AND THE IRS'S OWN FORM, FILLED (issue #43). Download Filled Form 941 asks
 * GET /payroll/forms/941.pdf for the quarter on show and saves what comes
 * back: the IRS's PDF with these figures in it and the header from Settings'
 * Business Details (issue #44), fields left editable for the signature and
 * anything Settings does not hold. A refusal -- a draft run, a changed IRS
 * file, the IRS unreachable -- is the server's sentence, shown under the
 * button, and the worksheet below stays.
 *
 * EVERY FIGURE SHOWS WHERE IT CAME FROM. The posted runs counted, each linking
 * to its run, and the payments on line 13, each linking to its entry. What is
 * left out is said, not hidden: draft runs, pay the books hold beyond the runs,
 * and anything paid on the federal accounts that the plan applied to no
 * quarter.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type Form941Report, type WorksheetQuarter } from '../api.js';
import { formatCents } from '../lib/money.js';
import { settingsTab } from '../lib/settings-tabs.js';

/** The day of the quarter's last month whose pay period line 1 asks about. */
const TWELFTH = ['March 12', 'June 12', 'September 12', 'December 12'] as const;

const MONTHS = [
  ['January', 'February', 'March'],
  ['April', 'May', 'June'],
  ['July', 'August', 'September'],
  ['October', 'November', 'December'],
] as const;

function Money({ cents }: { cents: number }) {
  return <>{formatCents(cents)}</>;
}

export function Form941Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const quarterParam = searchParams.get('quarter');
  const year = yearParam === null ? undefined : Number(yearParam);
  const quarter = quarterParam === null ? undefined : Number(quarterParam);
  const [report, setReport] = useState<Form941Report | null>(null);
  const [quarters, setQuarters] = useState<WorksheetQuarter[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getForm941(year, quarter)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setQuarters(result.quarters);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not build the worksheet.');
      });
    return () => {
      live = false;
    };
  }, [year, quarter]);

  async function downloadFilled(year: number, quarter: number) {
    setDownloading(true);
    setDownloadError(null);
    try {
      const file = await api.downloadForm941Pdf(year, quarter);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form-941-${year}-q${quarter}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked later rather than at once, as the books export does.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setDownloadError(caught instanceof ApiError ? caught.message : 'Could not fill the form.');
    } finally {
      setDownloading(false);
    }
  }

  function choose(value: string) {
    const [nextYear, nextQuarter] = value.split('-');
    const params = new URLSearchParams(searchParams);
    params.set('year', nextYear!);
    params.set('quarter', nextQuarter!);
    setSearchParams(params, { replace: true });
  }

  /*
   * The server's list, anchored on today and newest first. Built here from the
   * year on show it offered that year and the one before, so a quarter of last
   * year, once chosen, took this year off the list (reported 2026-09-23).
   */
  const choices = quarters.map((each) => ({ value: `${each.year}-${each.quarter}`, label: each.label }));

  return (
    <section className="form-941">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>Form 941 Worksheet</h1>

      {choices.length > 0 && report !== null && (
        <div className="row report-period">
          <label>
            Quarter{' '}
            <select value={`${report.quarter.year}-${report.quarter.quarter}`} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={downloading}
            onClick={() => void downloadFilled(report.quarter.year, report.quarter.quarter)}
          >
            {downloading ? 'Filling…' : 'Download Filled Form 941'}
          </button>
          <InfoNote label="What the Filled Form Leaves to You">
            The IRS's own Form 941, fetched from irs.gov and filled with this worksheet's figures: the quarter, lines 1
            to 15 and line 16, and the header, your EIN, name and address, from Business Details in Settings. A detail
            not entered there is left blank. Line 15's refund choice, Parts 3 to 5 and the signature are left for you;
            every field stays editable. A semiweekly depositor still attaches Schedule
            B. The form is only filled when it is exactly the IRS file this application was built against; if the IRS
            has revised it, the application says so and fills nothing.
          </InfoNote>
        </div>
      )}
      {downloadError && (
        <p role="alert" className="error">
          {downloadError}
        </p>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {report === null && error === null && <p className="muted">Loading…</p>}

      {report !== null && error === null && <Worksheet report={report} />}
    </section>
  );
}

function Worksheet({ report }: { report: Form941Report }) {
  const { worksheet: w, quarter } = report;
  const difference = report.books.ledgerLiabilityCents - report.books.runsLiabilityCents;

  return (
    <>
      <p className="notice">
        A worksheet: the figures to copy onto Form 941 for {quarter.label}, drawn from the quarter's posted pay runs.
        Nothing here files the return or pays anything; the return is yours to file with the IRS.
      </p>

      {report.draftRuns.length > 0 && (
        <div className="notice notice-warn">
          {report.draftRuns.length === 1 ? 'One run in this quarter is' : `${report.draftRuns.length} runs in this quarter are`}{' '}
          still a draft and not counted:
          <ul className="missed-list">
            {report.draftRuns.map((run) => (
              <li key={run.runId}>
                <Link to={`/payroll/runs/${run.runId}`}>
                  {run.employeeName}, paid {run.payDate}
                </Link>
              </li>
            ))}
          </ul>
          Post {report.draftRuns.length === 1 ? 'it' : 'them'}, then read this worksheet again.
        </div>
      )}

      {difference !== 0 && (
        <p className="notice notice-warn">
          The books hold {formatCents(report.books.ledgerLiabilityCents)} of Form 941 liability for {quarter.label}, and the
          quarter's runs {formatCents(report.books.runsLiabilityCents)}.{' '}
          {difference > 0
            ? 'Pay entered outside a run is not on this worksheet.'
            : "A run's entry no longer books everything the run calculated."}{' '}
          The worksheet is the runs'; decide which is right before filing.
        </p>
      )}

      <section className="settings-section" aria-labelledby="form-941-part1">
        <h2 id="form-941-part1">
          Part 1{' '}
          <InfoNote label="What the Zero Lines Are">
            Lines 4, 5b, 5f, 8, 9 and 11 are for wages exempt from Social Security and Medicare, tips, a Section 3121(q)
            notice, third-party sick pay, group-term life insurance for former employees and the research credit. None
            of them is in this application's runs. Line 1 counts the employees a posted run paid for the pay period
            including {TWELFTH[quarter.quarter - 1]}, each run's period taken from its frequency: a quarterly run's is
            its calendar quarter, a monthly run's its month, and a weekly or biweekly run's the seven or fourteen days
            ending on its pay date. Check line 1 if a pay period ends on another day.
          </InfoNote>
        </h2>
        <div className="table-scroll">
          <table className="lines" aria-label="Part 1">
            <thead>
              <tr>
                <th>Line</th>
                <th>On the Form</th>
                <th className="num">Column 1</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>Employees who received wages for the pay period including {TWELFTH[quarter.quarter - 1]}</td>
                <td />
                <td className="num">{w.line1}</td>
              </tr>
              <Row line="2" text="Wages, tips, and other compensation" cents={w.line2} />
              <Row line="3" text="Federal income tax withheld from wages, tips, and other compensation" cents={w.line3} />
              <tr>
                <td>4</td>
                <td>No wages subject to Social Security or Medicare tax</td>
                <td />
                <td className="num">{w.line4 ? 'Check' : 'Leave blank'}</td>
              </tr>
              <Row line="5a" text="Taxable social security wages, × 0.124" column1={w.line5a.wagesCents} cents={w.line5a.taxCents} />
              <Row line="5b" text="Taxable social security tips, × 0.124" column1={w.line5b.wagesCents} cents={w.line5b.taxCents} />
              <Row line="5c" text="Taxable Medicare wages & tips, × 0.029" column1={w.line5c.wagesCents} cents={w.line5c.taxCents} />
              <Row
                line="5d"
                text="Taxable wages & tips subject to Additional Medicare Tax withholding, × 0.009"
                column1={w.line5d.wagesCents}
                cents={w.line5d.taxCents}
              />
              <Row line="5e" text="Total social security and Medicare taxes" cents={w.line5e} />
              <Row line="5f" text="Section 3121(q) Notice and Demand" cents={w.line5f} />
              <Row line="6" text="Total taxes before adjustments" cents={w.line6} />
              <Row line="7" text="Current quarter's adjustment for fractions of cents" cents={w.line7} />
              <Row line="8" text="Current quarter's adjustment for sick pay" cents={w.line8} />
              <Row line="9" text="Current quarter's adjustments for tips and group-term life insurance" cents={w.line9} />
              <Row line="10" text="Total taxes after adjustments" cents={w.line10} />
              <Row line="11" text="Qualified small business payroll tax credit" cents={w.line11} />
              <Row line="12" text="Total taxes after adjustments and nonrefundable credits" cents={w.line12} />
              <Row line="13" text="Total deposits for this quarter" cents={w.line13} />
              <Row line="14" text="Balance due" cents={w.line14} />
              <Row line="15a" text="Overpayment" cents={w.line15a} />
            </tbody>
          </table>
        </div>
      </section>

      <Line16 report={report} />

      <section className="settings-section" aria-labelledby="form-941-evidence">
        <h2 id="form-941-evidence">Where the Figures Come From</h2>
        {report.runs.length === 0 ? (
          <p className="muted">No posted pay runs in {quarter.label}.</p>
        ) : (
          <div className="table-scroll">
            <table className="lines" aria-label="Runs Counted">
              <thead>
                <tr>
                  <th>Pay Date</th>
                  <th>Employee</th>
                  <th className="num">Form 941 Liability</th>
                </tr>
              </thead>
              <tbody>
                {report.runs.map((run) => (
                  <tr key={run.runId}>
                    <td className="date-cell">
                      <Link to={`/payroll/runs/${run.runId}`}>{run.payDate}</Link>
                    </td>
                    <td>{run.employeeName}</td>
                    <td className="num">
                      <Money
                        cents={run.incomeTaxWithheldCents + run.socialSecurityTaxCents + run.medicareTaxCents + run.additionalMedicareTaxCents}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {report.deposits.length === 0 ? (
          <p className="muted">Line 13: no payment has been applied to {quarter.label} yet.</p>
        ) : (
          <>
            <p className="muted">Line 13 is these payments, as the deposit tracker applied them:</p>
            <ul className="history-list" aria-label="Deposits">
              {report.deposits.map((deposit) => (
                <li key={deposit.headerId}>
                  <Link to={`/journals/${deposit.headerId}`}>
                    {deposit.counterparty ?? 'A payment'} on <span className="date-cell">{deposit.paidOn}</span>
                  </Link>{' '}
                  · {formatCents(deposit.cents)}
                  {!deposit.posted && ' (a draft)'}
                </li>
              ))}
            </ul>
          </>
        )}
        {report.unappliedFederalCents > 0 && (
          <p className="muted">
            {formatCents(report.unappliedFederalCents)} was paid on the federal accounts beyond everything the books accrued,
            so the tracker applied it to no quarter. If it was a deposit for {quarter.label}, it belongs on line 13 too.
          </p>
        )}
      </section>
    </>
  );
}

function Row({ line, text, column1, cents }: { line: string; text: string; column1?: number; cents: number }) {
  return (
    <tr>
      <td>{line}</td>
      <td>{text}</td>
      <td className="num">{column1 === undefined ? null : <Money cents={column1} />}</td>
      <td className="num">
        <Money cents={cents} />
      </td>
    </tr>
  );
}

function Line16({ report }: { report: Form941Report }) {
  const { line16 } = report.worksheet;
  const months = MONTHS[report.quarter.quarter - 1];

  /*
   * In a scroller, as every table here is: at 390 the long first column pushed
   * the amounts past the card's edge, where they were cut off rather than
   * scrolled to -- "$9,649.9" -- and the page-width probe cannot see a clip.
   */
  const monthTable = (
    <div className="table-scroll">
      <table className="lines" aria-label="Line 16 by Month">
        <tbody>
          {line16.months.map((cents, index) => (
            <tr key={months[index]}>
              <td>
                Month {index + 1}, {months[index]}
              </td>
              <td className="num">
                <Money cents={cents} />
              </td>
            </tr>
          ))}
          <tr>
            <th scope="row">Total liability for quarter</th>
            <td className="num">
              <Money cents={line16.total} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="settings-section" aria-labelledby="form-941-line16">
      <h2 id="form-941-line16">Part 2, Line 16</h2>
      {line16.box === 'de_minimis' && (
        <>
          <p>
            Check the first box: line 12 is under $2,500, or the prior quarter's was, and no day reached $100,000. Then go
            to Part 3.
          </p>
          {line16.recordRequired && (
            <>
              <p className="notice notice-warn">
                Line 12 is $100,000 or more, so the liability is still recorded by month, and on Schedule B if the business
                is a semiweekly depositor.
              </p>
              {monthTable}
            </>
          )}
        </>
      )}
      {line16.box === 'monthly' && (
        <>
          <p>Check the second box, a monthly schedule depositor, and enter the liability by the month wages were paid:</p>
          {monthTable}
        </>
      )}
      {line16.box === 'semiweekly' && (
        <>
          <p>
            Check the third box and complete Schedule B (Form 941) for the whole quarter, by the dates wages were paid:
          </p>
          <div className="table-scroll">
            <table className="lines" aria-label="Schedule B">
              <tbody>
                {line16.days.map((day) => (
                  <tr key={day.date}>
                    <td className="date-cell">{day.date}</td>
                    <td className="num">
                      <Money cents={day.cents} />
                    </td>
                  </tr>
                ))}
                <tr>
                  <th scope="row">Total liability for the quarter</th>
                  <td className="num">
                    <Money cents={line16.total} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
      {line16.box === null && (
        <p className="notice notice-warn">
          Which box depends on the deposit schedule, and none is recorded for {report.quarter.label}.{' '}
          <Link to={settingsTab('payroll')}>Record the Deposit Schedule</Link>
        </p>
      )}
    </section>
  );
}
